import { useShoppingContext } from '@/context/ShoppingContext';
import { getProductById, getStoreById } from '@/data/mockData';
import { Heart, Trash2, Upload, Clock, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';

export default function SavedListsPage() {
  const {
    savedLists, loadList, deleteSavedList,
    favoriteProducts, toggleFavoriteProduct,
    favoriteStores, toggleFavoriteStore,
  } = useShoppingContext();

  const favProducts = favoriteProducts.map(id => getProductById(id)).filter(Boolean);
  const favStores = favoriteStores.map(id => getStoreById(id)).filter(Boolean);

  return (
    <div className="container py-8 pb-24 md:pb-8">
      <Seo
        title="Saved lists & favorites | Grozo"
        description="Your saved grocery lists, favorite products, and favorite stores on Grozo."
        path="/saved"
        noindex
      />
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-destructive/20 to-destructive/5">
            <Heart className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Saved & Favorites</h1>
            <p className="text-sm text-muted-foreground">Your saved lists, favorite products, and stores</p>
          </div>
        </div>
      </div>

      {/* Saved lists */}
      <section className="mb-10">
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Saved Lists
        </h2>
        {savedLists.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border bg-card p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <ShoppingCart className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm mb-4">No saved lists yet. Build a list and save it for later!</p>
            <Button asChild variant="outline" size="sm" className="gap-1.5 rounded-xl">
              <Link to="/compare"><ShoppingCart className="h-3.5 w-3.5" /> Start Shopping</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {savedLists.map((list, i) => (
              <div key={list.id} className={`animate-slide-up stagger-${i + 1} hover-lift rounded-2xl border border-border bg-card p-5 shadow-sm`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-card-foreground">{list.name}</h3>
                    <p className="text-xs text-muted-foreground">{list.items.length} items · {new Date(list.createdAt).toLocaleDateString()}</p>
                  </div>
                  <Button variant="ghost" size="icon" aria-label="Delete list" className="h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive" onClick={() => deleteSavedList(list.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {list.items.slice(0, 4).map(item => {
                    const p = getProductById(item.productId);
                    return <Badge key={item.productId} variant="secondary" className="text-[10px] rounded-lg">{p?.image} {p?.name}</Badge>;
                  })}
                  {list.items.length > 4 && <Badge variant="secondary" className="text-[10px] rounded-lg">+{list.items.length - 4} more</Badge>}
                </div>
                <Button size="sm" variant="outline" className="w-full gap-1.5 rounded-xl" onClick={() => loadList(list)}>
                  <Upload className="h-3.5 w-3.5" />
                  Load List
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Favorite products */}
      <section className="mb-10">
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Heart className="h-4 w-4 text-destructive" />
          Favorite Products
        </h2>
        {favProducts.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-2xl border border-dashed border-border bg-card p-6 text-center">No favorite products yet. Tap the heart on any product card.</p>
        ) : (
          <div className="flex flex-wrap gap-2.5">
            {favProducts.map(p => p && (
              <div key={p.id} className="hover-lift flex items-center gap-2.5 rounded-2xl border border-border bg-card px-4 py-2.5 text-sm shadow-sm">
                <span className="text-lg">{p.image}</span>
                <span className="text-card-foreground font-medium">{p.name}</span>
                <button onClick={() => toggleFavoriteProduct(p.id)} aria-label={`Remove ${p.name} from favorites`} className="text-destructive hover:text-destructive/70 transition-colors">
                  <Heart className="h-3.5 w-3.5 fill-destructive" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Favorite stores */}
      <section>
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Heart className="h-4 w-4 text-destructive" />
          Favorite Stores
        </h2>
        {favStores.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-2xl border border-dashed border-border bg-card p-6 text-center">No favorite stores yet. Visit the Stores page to pick your favorites.</p>
        ) : (
          <div className="flex flex-wrap gap-2.5">
            {favStores.map(s => s && (
              <div key={s.id} className="hover-lift flex items-center gap-2.5 rounded-2xl border border-border bg-card px-4 py-2.5 text-sm shadow-sm">
                <span className="text-lg">{s.logo}</span>
                <span className="text-card-foreground font-medium">{s.name}</span>
                <button onClick={() => toggleFavoriteStore(s.id)} aria-label={`Remove ${s.name} from favorites`} className="text-destructive hover:text-destructive/70 transition-colors">
                  <Heart className="h-3.5 w-3.5 fill-destructive" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
